import React from 'react';
import reactHtmlParser from 'react-html-parser';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import * as RootActions from '../../actions/index';
import * as MailActions from '../../actions/mail';
import * as FolderActions from '../../actions/folders';
import * as FileActions from '../../actions/files';
import Svg from '../shared/Svg';
import colors from '../../utils/colors';
import { FolderItem } from '../shared/FolderItem';
import { NavigateFolder } from './NavigateFolder';
import { NavigateFile } from './NavigateFile';
import { FolderBreadcrumbs } from './FolderBreadcrumbs';
import { Search } from '../shared/Search';
import { FoldersSearchResults } from './FoldersSearchResults';
import { Attach } from './Attach';
import { isChangingEmailSaveLocation } from '../../actions/mail';
import ObjectIcon from '../shared/ObjectIcon';
import Progress from '../shared/Progress';

import './FoldersContainer.scss';
import { getFolders } from '../../utils/folders';
import { loadFiles } from '../../utils/files';

export const FoldersContainer = () => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  // Determine if we are searching for files or folders.
  const isComposingEmail = useSelector((state) => state.mail.isComposingEmail);
  const isChangingEmailLocation = useSelector((state) => state.mail.isChangingEmailSaveLocation);
  const isSearchingFiles = isComposingEmail && !isChangingEmailLocation;
  const navigatingFiles = useSelector((state) => state.files.navigatingDocrioFiles);
  const currentFileId = useSelector((state) => state.files.currentFileId);
  const currentFile = useSelector((state) => state.files.docrioFiles.find((docrioFile) => docrioFile.id === currentFileId));
  const emailId = useSelector((state) => state.mail.activeEmail);
  const draftId = useSelector((state) => state.mail.draftId);
  const isEml = currentFileId === emailId || currentFileId === draftId;

  const fileMaps = useSelector((state) => state.files.fileMaps);
  const folderMaps = useSelector((state) => state.folders.folderMaps);
  const folderMapsForCurrentFile = currentFile?.folderPaths || {};
  const allSelectedRecords = useSelector((state) => state.mail.selectedRecords);
  const docrioNamespace = useSelector((state) => state.mail.settings?.override?.docrioNamespace || 'litify_docs');
  let selectedRecords = allSelectedRecords;
  if (!navigatingFiles) {
    selectedRecords = Object.keys(allSelectedRecords)
        .filter((key) => Object.keys(folderMapsForCurrentFile).includes(key) && (isComposingEmail || !Array.isArray(allSelectedRecords[key].attachments) || !allSelectedRecords[key].attachments?.find((attachment) => attachment[`${docrioNamespace}__File_Data_Hash__c`] === currentFile.hash)))
        .reduce((obj, key) => {
          obj[key] = allSelectedRecords[key];
          return obj;
        }, {});
  }
  const displaySettings = useSelector((state) => state.mail.settings.objectsAndDisplayFields);
  const defaultFolderSettings = useSelector((state) => state.mail.settings.docrioSettings.defaultFolderSettings);

  const filesMaps = useSelector((state) => state.files.fileMaps);
  const [searchQuery, updateSearchQuery] = useState('');

  const [currentRecordContext, setCurrentRecordContext] = useState(Object.keys(selectedRecords)[0]);
  const selectedRecord = selectedRecords[currentRecordContext ? currentRecordContext : Object.keys(selectedRecords)[0]];
  const [folderPath, updateFolderPath] = useState(folderMaps?.[selectedRecord?.Id]?.[currentFolderPath]?.path ? currentFolderPath : '');
  const [selectedRecordFolderMap, setSelectedRecordFolderMap] = useState(folderMaps?.[selectedRecord?.Id] || {});
  const displayField = displaySettings.find((setting) => setting.apiName === selectedRecord.type)?.displayFields[0].apiName;

  const currentFileFolderPath = useSelector((state) => state.files.docrioFiles.find((file) => file.id === currentFileId)?.folderPath);
  const isLoading = useSelector((state) => state.app.isLoading);

  // If are searching files, assume to always use the default/root folder path on load.
  const currentFolderPath = useSelector((state) => {
    if (isSearchingFiles) {
      return isEml ? defaultFolderSettings[selectedRecord?.type]?.saveEmailFolderPath : defaultFolderSettings[selectedRecord?.type]?.saveAttachmentsFolderPath; // TODO: may need update in https://litify-central.atlassian.net/browse/EM-1999
    }
    return currentFileFolderPath;
  });

  const populateFolderMap = async () => {
    const folderMap = await getFolders(selectedRecord?.Id, defaultFolderSettings[selectedRecord?.type]?.objectLabel, docrioNamespace);

    batch(() => {
      dispatch(FolderActions.setFolderMaps({ ...folderMaps, [selectedRecord?.Id]: folderMap }));
      dispatch(MailActions.updateRecords([{ ...selectedRecord, fetchedFolders: true }]));
    });

    dispatch(RootActions.isLoading(false));
  };

  const populateFileMap = async () => {
    let nextBatch;
    const fetchedFiles = fileMaps[selectedRecord?.Id] || {};
    do {
      const { mappedFiles, nextFileBatch } = await loadFiles(selectedRecord.Id, nextBatch, docrioNamespace);
      const folders = Object.keys(mappedFiles || {});
      for (let i = 0; i < folders.length; i++) {
        const folder = folders[i];
        // If the folder already exists, append new file data
        if (fetchedFiles[folder]) {
          fetchedFiles[folder] = [...fetchedFiles[folder], ...mappedFiles[folder]];
        } else {
          // If the folder doesn't exist, add it to the object
          fetchedFiles[folder] = mappedFiles[folder];
        }
      }
      dispatch(FileActions.setFileMaps({ ...fileMaps, [selectedRecord?.Id]: fetchedFiles }));
      if (!nextBatch) {
        batch(() => {
          dispatch(RootActions.isLoading(false));
          dispatch(MailActions.updateRecords([{ ...selectedRecord, fetchedFolders: true, fetchingFiles: true }]));
        });
      }
      nextBatch = nextFileBatch;
    } while (nextBatch);

    dispatch(MailActions.updateRecords([{ ...selectedRecord, fetchedFolders: true, fetchedFiles: true }]));
  };

  const populateFoldersAndFilesMap = async () => {
    const folderMap = await getFolders(selectedRecord?.Id, defaultFolderSettings[selectedRecord?.type]?.objectLabel, docrioNamespace);
    batch(() => {
      dispatch(FolderActions.setFolderMaps({ ...folderMaps, [selectedRecord?.Id]: folderMap }));
      dispatch(MailActions.updateRecords([{ ...selectedRecord, fetchedFolders: true }]));
    });
    await populateFileMap(selectedRecord.Id);
  };

  useEffect(() => {
    if (selectedRecord.fetchedFiles || selectedRecord.fetchingFiles) {
      return;
    } else if (isSearchingFiles && selectedRecord.fetchedFolders) {
      dispatch(RootActions.isLoading(true));
      populateFileMap();
    } else if (isSearchingFiles) {
      dispatch(RootActions.isLoading(true));
      populateFoldersAndFilesMap();
    } else if (!selectedRecord.fetchedFolders) {
      dispatch(RootActions.isLoading(true));
      populateFolderMap();
    }
  }, [selectedRecord?.Id]);

  useEffect(() => {
    setSelectedRecordFolderMap(folderMaps?.[selectedRecord?.Id] || {});
  }, [folderMaps, selectedRecord?.Id]);

  const onBackClicked = () => {
    batch(() => {
      dispatch(isChangingEmailSaveLocation(false));
      dispatch(FolderActions.navigatingFolders(false));
      dispatch(FileActions.navigatingDocrioFiles(false));
    });
  };

  const searchFiles = () => {
    const resultsMap = [];
    if (!folderPath.length) {
      for (const value of Object.values(filesMaps?.[selectedRecord.Id])) {
        const searchResult = value.filter((file) => file.Name.toLowerCase().includes(searchQuery.toLowerCase()));
        searchResult.length && resultsMap.push(searchResult);
      }
    } else {
      // get all keys that contain the folderPath
      const folderKeys = Object.keys(filesMaps?.[selectedRecord.Id]).filter((key) => key.includes(folderPath));
      for (const folderPath of folderKeys) {
        const results = fileMaps?.[selectedRecord.Id]?.[folderPath]?.filter((file) => file.Name.toLowerCase().includes(searchQuery.toLowerCase()));
        results.length && resultsMap.push(results);
      }
    }
    return resultsMap.flat();
  };

  const searchFolders = () => {
    const searchResult = Object.entries(selectedRecordFolderMap).filter(([_, value]) => value.path !== '' && value.name.toLowerCase().includes(searchQuery.toLowerCase())).map((result) => selectedRecordFolderMap[result[1].path]);
    return searchResult.flat();
  };

  const [placeholder, handleSearch] = isSearchingFiles ? ['Search for files...', searchFiles] : ['Search for folder...', searchFolders];

  const searchResults = useMemo(() => !!searchQuery.length && handleSearch(), [searchQuery]);

  const getFolderMappedToNavigateElements = (foldersArray = []) => {
    const mappedFolders = [];
    for (const subfolder of foldersArray) {
      if (subfolder?.isRoot && navigatingFiles) {
        continue;
      } else {
        mappedFolders.push(<NavigateFolder
          key={subfolder?.path}
          folder={folderMaps?.[selectedRecord?.Id]?.[subfolder.path]}
          updateBrowsingFolderCallback={updateFolderPath}
          clearSearchCallback={updateSearchQuery}
          showSelectFolder={!isSearchingFiles}
          isRoot={subfolder?.isRoot}
          isSearchingFiles={isSearchingFiles}
          selectedRecord={selectedRecord}
        />);
      }
    }
    return mappedFolders;
  };

  const getFileMappedToNavigateElements = (filesArray) => filesArray?.map((file) =>
    <NavigateFile
      key={file.Id}
      file={file}
    />);

  const getNavigateElements = () => searchResults ? getSearchElements() : getMappedNavigationElements();

  const getSearchElements = () => {
    return isSearchingFiles ? getFileMappedToNavigateElements(searchResults) : getFolderMappedToNavigateElements(searchResults);
  };

  const getMappedNavigationElements = () => {
    const subFolders = folderPath === '' ? [{ ...selectedRecordFolderMap[folderPath], isRoot: true }].concat(selectedRecordFolderMap[folderPath]?.subfolders) : selectedRecordFolderMap[folderPath]?.subfolders;
    const folderElements = getFolderMappedToNavigateElements;
    return isSearchingFiles ? folderElements(subFolders).concat(getFileMappedToNavigateElements(filesMaps?.[selectedRecord?.Id]?.[folderPath])) : folderElements(subFolders);
  };

  const setIsExpandedHelper = () => setIsExpanded((prev) => !prev);

  const updateSelectedRecordHelper = (recordId) => {
    setIsExpandedHelper();
    updateSearchQuery('');
    setCurrentRecordContext(recordId);
  };

  const folderSearchContainer = useMemo(() => {
    if (selectedRecord && Object.keys(selectedRecord)?.length && selectedRecordFolderMap && Object.keys(selectedRecordFolderMap)?.length) {
      return (
        <>
          <FolderBreadcrumbs path={folderPath} updateBrowsingFolderCallback={updateFolderPath} folderMap={selectedRecordFolderMap} selectedRecord={selectedRecord} isSearchingFiles={isSearchingFiles}/>
          <FoldersSearchResults innerComponents={getNavigateElements()} />
        </>
      );
    }
  }, [selectedRecord, searchResults, filesMaps, folderPath, selectedRecordFolderMap]);

  useEffect(() => {
    updateFolderPath(selectedRecordFolderMap[currentFolderPath]?.path ? currentFolderPath : '');
  }, [folderMaps]);

  // If there are no folders in the folderMaps, assume folders are loading and render the progress spinner.
  if (Object.keys(folderMaps).length === 0 || (isComposingEmail && !filesMaps && navigatingFiles)) {
    return <Progress />;
  }

  return (
    <div className={`folders-container ${isLoading && 'deactivate'}`}>
      <div className='folders-back-container'>
        <button // back button out of line on left side compared to mock
          className='folders-back'
          onClick={onBackClicked}
        >
          <Svg
            className='folders-back-icon'
            data='/assets/back.svg'
            fill={colors.strongBlue}
            onClick={onBackClicked}
          />
        Back
        </button>
      </div>
      <FolderItem
        image={<ObjectIcon objectApiName={selectedRecord.type} className='folders-image' />}
        content={<p>{reactHtmlParser(selectedRecord[displayField])}</p>}
        className={`folders-saved-location ${Object.values(selectedRecords)?.length > 1 && isExpanded ? 'active' : ''}`}
        id='selected-record'
        button={Object.values(selectedRecords)?.length > 1 ? <Svg
          className='folders-down-icon'
          data='/assets/chevronFull.svg'
          fill={colors.grey}
          onClickAction={setIsExpandedHelper}
        /> : ''}
      />
      <>
        {isExpanded &&
        <div>
          <div className='folders-selected-folders'>
            {Object.values(selectedRecords).filter((record) => record.Id !== selectedRecord.Id).map((record, index) => (
              <div className={'folders-saved-location'} id='selected-record' key={index} onClick={() => updateSelectedRecordHelper(record.Id)}>
                <ObjectIcon objectApiName={record.type} className='folders-image' />
                <p>{reactHtmlParser(record[displaySettings.find((setting) => setting.apiName === record.type).displayFields[0].apiName])}</p>
              </div>
            ))}
          </div>
        </div>
        }
      </>
      {isSearchingFiles && <Attach />}
      <div className='folders-search-container'>
        <Search
          className={'folders-search-input'}
          onSearchCallback={updateSearchQuery}
          searchPlaceholder={placeholder}
          searchQuery={searchQuery}
        />
      </div>
      {folderSearchContainer}
    </div>
  );
};
